import { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import JoditEditor from "jodit-react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { baseUrl } from "../../../helper/urls";
import { Icon } from "@iconify/react";
import { Box, Modal, Typography } from "@mui/material";

const CreatePolicy = () => {
  const { token } = useSelector((state) => state.auth);
  const editorRef = useRef(null);
  const [fieldName, setFieldName] = useState("");
  const [content, setContent] = useState("");
  const [policyData, setPolicyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPolicyType, setEditingPolicyType] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // start loading

    const editorContent = content;

    if (!fieldName) {
      toast.error("Please select a policy type.");
      setLoading(false); // stop loading on error
      return;
    }

    if (!editorContent.trim() || editorContent === "<p><br></p>") {
      toast.error("Policy description cannot be empty.");
      setLoading(false); // stop loading on error
      return;
    }

    try {
      const response = await axios.post(
        `${baseUrl}/api/policy/updateOrCreatePolicy`,
        { fieldName, value: editorContent },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        const successMessage = isEditing
          ? `${editingPolicyType
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) =>
                l.toUpperCase()
              )} policy updated successfully!`
          : `${fieldName
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) =>
                l.toUpperCase()
              )} policy created successfully!`;

        toast.success(successMessage, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Reset form after successful submission
        setFieldName("");
        setContent("");
        setIsEditing(false);
        setEditingPolicyType("");

        fetchPolicyList(); // Refresh data
      }

      setLoading(false); // ✅ Stop loading after success
    } catch (error) {
      setLoading(false); // ✅ Always stop loading on error

      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err) =>
          toast.error(err.msg || "Error", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          })
        );
      } else {
        toast.error("Something went wrong. Please try again.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }

      console.error(error);
    }
  };

  const handleEdit = (policyType, policyContent) => {
    setFieldName(policyType);
    setContent(policyContent || "");
    setIsEditing(true);
    setEditingPolicyType(policyType);
    // Scroll to top to show the form
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Show info toast when editing starts
    toast.info(
      `Now editing ${policyType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase())} policy`,
      {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    );
  };

  const handleCancel = () => {
    setFieldName("");
    setContent("");
    setIsEditing(false);
    setEditingPolicyType("");

    // Show info toast when editing is cancelled
    toast.info("Editing cancelled", {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const fetchPolicyList = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/policy/allPolicy`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setPolicyData(response.data.data || []);  
      setLoading(false);
    } catch (error) {
      console.error("Error fetching policies:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicyList();
  }, []);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 800,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 2,
  };

  const styles = {
    container: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
      padding: "16px",
      background: "#fff",
      borderRadius: "8px",
      marginBottom: "20px",
      alignItems: "flex-end",
    },
    group: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    label: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#333",
    },
    input: {
      padding: "6px 10px",
      fontSize: "14px",
      height: "36px",
      border: "1px solid #ccc",
      borderRadius: "6px",
      minWidth: "180px",
    },
    iconButton: {
      width: "36px",
      height: "36px",
      border: "none",
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      transition: "0.2s ease-in-out",
    },
    primary: {
      backgroundColor: "#e3f2fd",
      color: "#1976d2",
    },
    primaryHover: {
      backgroundColor: "#bbdefb",
    },
    secondary: {
      backgroundColor: "#fce4ec",
      color: "#d32f2f",
    },
    secondaryHover: {
      backgroundColor: "#f8bbd0",
    },
  };

  const policyCards = [
    {
      title: "Terms And Conditions",
      fieldName: "terms_and_conditions",
      data: policyData.terms_and_conditions,
    },
    {
      title: "Reschedule Cancellation",
      fieldName: "reschedule_cancellation",
      data: policyData.reschedule_cancellation,
    },
    {
      title: "Privacy Policy",
      fieldName: "privacy_policy",
      data: policyData.privacy_policy,
    },
    {
      title: "Refund Policy",
      fieldName: "refund",
      data: policyData.refund,
    },
  ];

  return (
    <>
      <ToastContainer />
      <div className="mt-4">
        <div className="row g-3">
          {policyCards?.map((policy, index) => (
            <div key={index} className="col-12 col-sm-6 col-lg-4 col-xl-3">
              <div className="card h-100 shadow-sm">
                <div className="card-body d-flex flex-column">
                  <h6
                    className="card-title text-truncate mb-3"
                    title={policy.title}
                  >
                    {policy.title}
                  </h6>
                  <div className="mt-auto">
                    <div className="d-flex flex-column flex-sm-row gap-2">
                      <button
                        className="btn btn-sm flex-fill"
                        onClick={() =>
                          handleEdit(policy.fieldName, policy.data)
                        }
                        style={{ border: "1px solid #124178" }}
                      >
                        <Icon icon="mdi:pencil" className="me-1" />
                        Edit
                      </button>
                      <button
                        className="btn btn-sm flex-fill"
                        onClick={() => {
                          setSelectedPolicy(policy.data);
                          setShowModal(true);
                        }}
                        style={{ border: "1px solid #124178" }}
                      >
                        <Icon icon="mdi:eye" className="me-1" />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="row gy-4">
        <div className="col-lg-12">
          <div className="card mt-24">
            <div className="card-header border-bottom">
              <h6 className="text-xl mb-0">
                {isEditing
                  ? `Edit ${editingPolicyType
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}`
                  : "Add New Policy"}
              </h6>
              {isEditing && (
                <small className="text-muted d-block mt-1">
                  You are currently editing an existing policy
                </small>
              )}
            </div>
            <div className="card-body p-24">
              <form
                onSubmit={handleSubmit}
                className="d-flex flex-column gap-20"
              >
                <div>
                  <label className="form-label fw-bold text-neutral-900">
                    Select Policy
                  </label>
                  <select
                    className="form-control border border-neutral-200 radius-8"
                    value={fieldName}
                    onChange={(e) => setFieldName(e.target.value)}
                    required
                    disabled={isEditing}
                  >
                    <option value="">-- Select Policy --</option>
                    <option value="terms_and_conditions">
                      Terms And Conditions
                    </option>
                    <option value="reschedule_cancellation">
                      Reschedule Cancellation
                    </option>
                    <option value="privacy_policy">Privacy Policy</option>
                    <option value="refund">Refund Policy</option>
                  </select>
                </div>

                <div>
                  <label className="form-label fw-bold text-neutral-900">
                    Policy Description
                  </label>
                  <div className="border border-neutral-200 radius-8 overflow-hidden">
                    <JoditEditor
                      ref={editorRef}
                      value={content}
                      tabIndex={1}
                      onChange={(newContent) => setContent(newContent)}
                    />
                  </div>
                </div>

                <div className="row justify-content-end">
                  <div className="col-auto">
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-secondary radius-8 me-2"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      className="btn btn-primary-600 radius-8"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Loading...
                        </>
                      ) : isEditing ? (
                        "Update"
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </div>

                {/* <div className="row justify-content-end">
                  <div className="col-auto">
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-secondary radius-8 me-2"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      className="btn btn-primary-600 radius-8"
                    >
                      {isEditing ? "Update" : "Submit"}
                    </button>
                  </div>
                </div> */}
              </form>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        aria-labelledby="summary-modal-title"
        aria-describedby="summary-modal-description"
      >
        <Box
          sx={{
            ...modalStyle,
            position: "relative",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <button
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              ...styles.iconButton,
              ...styles.secondary,
            }}
            onClick={() => setShowModal(false)}
            title="Close Modal"
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor =
                styles.secondaryHover.backgroundColor;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor =
                styles.secondary.backgroundColor;
            }}
          >
            <Icon icon="material-symbols:cancel-outline-rounded" />
          </button>
          <Typography id="summary-modal-description">
            <div dangerouslySetInnerHTML={{ __html: selectedPolicy }}></div>
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

export default CreatePolicy;
